import {Button, Card, Checkbox, Flex, Form, Input, message, Modal, Steps, Typography} from "antd";
import React, {useCallback, useEffect, useState} from "react";
import {useForm} from "antd/es/form/Form";
import {useAuthContext} from "./LauncherAuthContext";
import PhoneInput, {PhoneNumber} from "antd-phone-input";
import {Account, TwoFactorAuthStatusResponse, VerificationForm} from "./types";
import {useNavigate} from "react-router";
import {ProfileOutlined, SmileOutlined, SolutionOutlined} from "@ant-design/icons";
import {LauncherLayout} from "../layout/LauncherLayout";
import {checkNumber} from "../sms/sms-service";
import {initiateOtp, verifyAccount as verifyAccountRequest, verifyOtp} from "./verification-service";
import {LoadingIndicator} from "../../commons/Indicators";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import axios, {AxiosResponse} from "axios";
import {ExistsDto} from "../../events/types";
import {launcherCard} from "../common/styles";
import {useSearchParams} from "react-router-dom";
import {Label} from "../../commons/translations/Label";
import {LabelLink} from "../../commons/translations/LabelLink";
import {useTranslation} from "react-i18next";
import {phoneToString} from "../common/phone-tools";

export const SingUpPage: React.FC = () => {
    const {t} = useTranslation();
    const [form] = useForm<VerificationForm>();
    const [otpForm] = useForm<{otp: string}>();
    const { user, updateAccount } = useAuthContext();
    const navigate = useNavigate();
    const [verifying, setVerifying] = useState<boolean>(false);
    const [working, setWorking] = useState<boolean>(false);
    const [otpRef, setOtpRef] = useState<string | undefined>(undefined);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const redirect = searchParams.get("target") || '/';
        if (user?.status === "VERIFIED" && user.profile) {
            navigate(redirect);
        } else if (user?.status === "VERIFIED" && !user.profile) {
            navigate(`/profile?target=${redirect}`);
        } else {
            form.setFieldValue('nickname', user?.name);
            form.setFieldValue('userId', user?.id);
        }
    }, [user?.status, user?.name, searchParams]);

    const startOTP = useCallback((data: VerificationForm) => {
        if (working || !executeRecaptcha) {
            return;
        }

        setVerifying(true);
        setWorking(true);
        const value = data.mobile;
        const number = phoneToString(value);
        executeRecaptcha('sendOtp').then((captcha) => {
            initiateOtp({
                captcha,
                number: number
            }).then((response) => {
                setOtpRef(response.refCode);
                setWorking(false);
            })
            .catch(() => {
                setOtpRef(undefined);
                setWorking(false);
                message.error(t('verification.errors.invalidCode')).then(() => {
                    setVerifying(false);
                })
            })
        })

    }, [setOtpRef, setWorking, setVerifying, initiateOtp, executeRecaptcha, t]);

    const verifyAccount = useCallback((input: {otp: string}) => {
        if (working || !executeRecaptcha || !otpRef) {
            return;
        }

        setWorking(true);
        const data = form.getFieldsValue();
        const value = data.mobile;
        const number = phoneToString(value);

        verifyOtp(otpRef, input.otp)
            .then((result: TwoFactorAuthStatusResponse) => {
                if (result.status === "VERIFIED") {
                    return Promise.resolve();
                } else if (result.status === "PENDING") {
                    setWorking(false);
                    return Promise.reject({
                        severity: 'warning',
                        message: t('verification.errors.invalidCode')
                    });
                } else {
                    return Promise.reject();
                }
            })
            .then(() => executeRecaptcha('verifyAccount'))
            .then((captcha) =>
                verifyAccountRequest({
                    captcha,
                    mobile: number,
                    userId: data.userId,
                    nickname: data.nickname,
                    privacy: data.privacy,
                    terms: data.terms,
                }
            ))
            .then((updated: Account) =>
                message.success(t('verification.success.verified'), 0.3).then(() => {
                    updateAccount(updated);
                    setWorking(false);
                })
            )
            .catch((reason: { severity: string, message: string } | undefined) => {
                setWorking(false);
                if (reason && reason.severity === 'warning') {
                    message.warning(reason.message);
                } else {
                    message.error(t('verification.errors.invalidCode'));
                }
            });
    }, [otpRef, setWorking, verifyAccountRequest, executeRecaptcha, updateAccount, t]);



    const validateNickname = useCallback((nickname: string, userId: string) => {
        if (!nickname) {
            return false;
        }
        return axios.get<object, AxiosResponse<ExistsDto>>(`/api/launcher/users/account/${userId}/nicknames/${nickname}`)
            .then((r) => {
                const response = r.data;
                return !response.exists ||
                    response.exists && !!userId && userId === response.entityId;
            })
    }, [])

    return (
        <LauncherLayout disableNavigation={true}>
            <Card style={{...launcherCard}}>
                <Modal open={verifying} closable={false} footer={null}>
                    {working && <LoadingIndicator />}
                    {!working &&
                        <>
                            <Typography.Title level={2}><Label trl="verification.form.code" /></Typography.Title>
                            <Typography.Text><Label trl="verification.success.codeSent" /></Typography.Text>
                            <Flex vertical={false} justify={"space-evenly"}>
                                <Form form={otpForm} onFinish={verifyAccount} layout={"horizontal"}>
                                    <Form.Item layout={"vertical"} label={<Label trl="verification.form.code" />} name="otp" rules={[{required:true}]}>
                                        <Input.OTP length={6} size={"large"}/>
                                    </Form.Item>
                                </Form>
                            </Flex>

                            <Flex vertical={false} justify={"space-evenly"} align={"start"}>
                                <Button type={"primary"} danger onClick={() => {setVerifying(false); }}><Label trl="common.cancel" /></Button>
                                <Button type={"primary"} onClick={otpForm.submit}><Label trl="verification.buttons.verify" /></Button>
                            </Flex>
                        </>
                    }
                </Modal>
                <Typography.Title level={2}><Label trl="verification.title" /></Typography.Title>
                <Steps
                    size={"small"}
                    items={[
                        {
                            title: t('verification.title'),
                            status: 'process',
                            icon: <SolutionOutlined />,
                        },
                        {
                            title: t('profile.player.title'),
                            status: 'wait',
                            icon: <ProfileOutlined />,
                        },
                        {
                            title: t('common.success'),
                            status: 'wait',
                            icon: <SmileOutlined />,
                        },
                    ]}
                />
                <Typography.Text>
                    <Label trl="verification.welcome" />
                </Typography.Text>
                <Form form={form} onFinish={startOTP} layout={"vertical"}>
                    <Form.Item hidden name={"userId"} />
                    <Form.Item label={<Label trl="verification.form.nickname" />}
                               name={"nickname"}
                               required={true}
                               hasFeedback={true}
                               validateDebounce={1000}
                               rules={[
                                   {
                                       validator: async (_, value) => {
                                           const isValid = await validateNickname(value, user!.id);
                                           if (isValid) {
                                               return Promise.resolve();
                                           } else {
                                               return Promise.reject(new Error('nickname.taken'));
                                           }

                                       }
                                   },
                                   {required: true}
                               ]}
                    >
                        <Input defaultValue={user?.name} placeholder={t('verification.form.nickname.placeholder')} />
                    </Form.Item>
                    <Form.Item label={<Label trl="verification.form.mobile" />} name={"mobile"} required={true} rules={[{ required:true}, {
                        validator: async (_, value: PhoneNumber) => {
                            if (value && phoneToString(value).length < 11) {
                                return  Promise.reject(t('phone.number.invalid'));
                            } else {
                                return  checkNumber(value, t);
                            }
                        }
                    }]}>
                        <PhoneInput />
                    </Form.Item>
                    <Form.Item label={<><Label trl={'verification.form.agree'} /> <LabelLink link={{
                        to: '/terms-and-conditions',
                        target: '_blank',
                    }} trl={'verification.form.terms'}/> </>} name={"terms"} valuePropName={"checked"}
                               required rules={[{ required:true }]}
                    >
                        <Checkbox />
                    </Form.Item>
                    <Form.Item label={<><Label trl={'verification.form.and'} /> <LabelLink link={{
                        to: '/privacy-policy',
                        target: '_blank',
                    }} trl={'verification.form.policy'}/> </>} name={"privacy"} valuePropName={"checked"}
                               required rules={[{ required:true }]}
                    >
                        <Checkbox />
                    </Form.Item>
                    <Button type={"primary"} style={{width: '100%'}} size={"large"} onClick={form.submit}>{t('verification.buttons.sendCode')}</Button>
                </Form>
            </Card>
        </LauncherLayout>
    );
}